import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormSetError, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import {
	addChangesHistory,
	deleteUserStories,
	deleteUserStoriesCriteria,
	deleteUserStoriesScenarios,
	editUserStories,
} from 'store/ticket-service/asyncActions';
import {
	getActiveSubStage,
	getActiveTicket,
	getDetailedViewTicketSummary,
	getTicketUpdating,
} from 'store/ticket-service/selector';
import {
	setDetailedViewTicketSummary,
	setEditMode,
	setIsTicketUpdate,
	updateLocalTicket,
} from 'store/ticket-service/ticketSlice';
import {
	ISummaryStoryWithId,
	IUserStoryAcceptanceCriteria,
	IUserStoryScenario,
} from 'store/ticket-service/types';
import { setIsOpenNoDeleteAccess, triggerTicketSave } from 'store/user-service/userSlice';

import { flexMixin } from 'utils/flexMixin';
import { getSortedSummary } from 'utils/getSortedSummary';

import {
	HistoryActionTypes,
	NamespacesList,
	SharePermissions,
	TicketStages,
} from 'core/enums/common';
import { editTitleSchema, editUserStoriesSchema } from 'core/helpers/yupSchemas';

import { FormTitleValue } from 'pages/Home';

import { TitleText } from 'components/shared/CustomText/TitleText';
import { ExpandableTextField } from 'components/shared/ExpandableTextField';

import { EditDetailedStoryView } from './DetailedStoryView/EditDetailedStoryView';
import { EditUserStoriesDropdown } from './EditUserStoriesDropdown';

export interface IFormUserStoryScenario extends IUserStoryScenario {
	isDeleted: boolean;
}

export interface IFormUserStoryAcceptanceCriteria extends IUserStoryAcceptanceCriteria {
	isDeleted: boolean;
}

export interface IFormSummaryStory {
	data: string;
	isDeleted: boolean;
	summaryScenarios?: Array<IFormUserStoryScenario>;
	summaryAcceptanceCriteria?: Array<IFormUserStoryAcceptanceCriteria>;
	id?: string;
}

interface FormValues {
	description: string;
	ticketSummaries: Array<IFormSummaryStory>;
}

interface EditTicketFormProps {
	ticketTitle: string;
	setTitleError: UseFormSetError<FormTitleValue>;
}

export const EditGeneralStep: FC<EditTicketFormProps> = ({ ticketTitle, setTitleError }) => {
	const { t: generalStepTranslate, i18n } = useTranslation(NamespacesList['generalStep']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();
	const activeTicket = useSelector(getActiveTicket);
	const updating = useSelector(getTicketUpdating);
	const detailedViewTicketSummary = useSelector(getDetailedViewTicketSummary);
	const activeSubStage = useSelector(getActiveSubStage);

	const [initialData, setInitialData] = useState<FormValues>({
		description: '',
		ticketSummaries: [],
	});

	const {
		control,
		handleSubmit,
		register,
		reset,
		formState: { errors },
		getValues,
	} = useForm<FormValues>({
		resolver: yupResolver(editUserStoriesSchema()),
		defaultValues: {
			description: '',
			ticketSummaries: [],
		},
	});

	const { fields: summaryStories } = useFieldArray({
		control,
		name: 'ticketSummaries',
	});

	useEffect(() => {
		if (activeTicket) {
			const updatedSummaries = getSortedSummary(activeTicket.ticketSummaries).map((summary) => ({
				...summary,
				isDeleted: false,
				summaryScenarios: summary.summaryScenarios
					? summary.summaryScenarios.map((scenario: IFormUserStoryScenario) => ({
							...scenario,
							isDeleted: false,
					  }))
					: [],
				summaryAcceptanceCriteria: summary.summaryAcceptanceCriteria
					? summary.summaryAcceptanceCriteria.map((criteria: IFormUserStoryAcceptanceCriteria) => ({
							...criteria,
							isDeleted: false,
					  }))
					: [],
			}));

			setInitialData({ description: activeTicket.description, ticketSummaries: updatedSummaries });

			reset({
				description: activeTicket.description,
				ticketSummaries: updatedSummaries,
			});
		}
	}, [activeTicket?.id]);

	const currentStepPermission = useMemo(() => {
		const currentStagePermission = activeTicket?.permissions?.filter(
			(permission) =>
				permission.ticketCurrentStep === TicketStages.general ||
				permission.ticketCurrentStep === null
		);

		if (currentStagePermission?.length) {
			const allSubStagesItem = currentStagePermission.find(
				(permission) => permission.subStageEnum === null
			);
			if (allSubStagesItem) {
				return allSubStagesItem.permission;
			} else {
				const currentSubStagePermission = currentStagePermission.find(
					(permission) => permission.subStageEnum === activeSubStage
				);

				return currentSubStagePermission?.permission || null;
			}
		}

		return null;
	}, [activeSubStage, activeTicket?.id, activeTicket?.permissions]);

	const currentSelectedSummary = useMemo(
		() =>
			getValues().ticketSummaries.find((summary) => summary?.id === detailedViewTicketSummary?.id),
		[summaryStories, initialData]
	);

	const currentSelectedSummaryIndex = useMemo(
		() =>
			initialData.ticketSummaries.findIndex(
				(summary) => summary?.id === detailedViewTicketSummary?.id
			),
		[summaryStories, initialData.ticketSummaries]
	);

	const handleDeleteSummary = (index: number) => {
		if (currentStepPermission === null || currentStepPermission === SharePermissions.Manage) {
			const updatedSummaryStories = getValues().ticketSummaries;
			updatedSummaryStories[index].isDeleted = true;

			reset({
				...getValues(),
				ticketSummaries: updatedSummaryStories,
			});
		} else {
			dispatch(setIsOpenNoDeleteAccess(true));
		}
	};

	const handleRefreshSummary = (index: number) => {
		const updatedSummaryStories = getValues().ticketSummaries;
		updatedSummaryStories[index].isDeleted = false;

		reset({
			...getValues(),
			ticketSummaries: updatedSummaryStories,
		});
	};

	const handleDeleteScenario = (index: number, itemIndex: number) => {
		if (currentStepPermission === null || currentStepPermission === SharePermissions.Manage) {
			const updatedSummaryStories = getValues().ticketSummaries;

			if (updatedSummaryStories?.[index]?.summaryScenarios) {
				// @ts-ignore
				updatedSummaryStories[index].summaryScenarios[itemIndex].isDeleted = true;

				reset({
					...getValues(),
					ticketSummaries: updatedSummaryStories,
				});
			}
		} else {
			dispatch(setIsOpenNoDeleteAccess(true));
		}
	};

	const handleRefreshScenario = (index: number, itemIndex: number) => {
		const updatedSummaryStories = getValues().ticketSummaries;

		if (updatedSummaryStories?.[index]?.summaryScenarios) {
			// @ts-ignore
			updatedSummaryStories[index].summaryScenarios[itemIndex].isDeleted = false;

			reset({
				...getValues(),
				ticketSummaries: updatedSummaryStories,
			});
		}
	};

	const handleDeleteAcceptanceCriteria = (index: number, itemIndex: number) => {
		if (currentStepPermission === null || currentStepPermission === SharePermissions.Manage) {
			const updatedSummaryStories = getValues().ticketSummaries;

			if (updatedSummaryStories?.[index]?.summaryAcceptanceCriteria) {
				// @ts-ignore
				updatedSummaryStories[index].summaryAcceptanceCriteria[itemIndex].isDeleted = true;

				reset({
					...getValues(),
					ticketSummaries: updatedSummaryStories,
				});
			}
		} else {
			dispatch(setIsOpenNoDeleteAccess(true));
		}
	};

	const handleRefreshAcceptanceCriteria = (index: number, itemIndex: number) => {
		const updatedSummaryStories = getValues().ticketSummaries;

		if (updatedSummaryStories?.[index]?.summaryAcceptanceCriteria) {
			// @ts-ignore
			updatedSummaryStories[index].summaryAcceptanceCriteria[itemIndex].isDeleted = false;

			reset({
				...getValues(),
				ticketSummaries: updatedSummaryStories,
			});
		}
	};

	const getDeletedTicketSummariesIds = (ticketSummaries: Array<IFormSummaryStory>) =>
		ticketSummaries.filter((summary) => summary.isDeleted).map((summary) => summary?.id || '');

	const getDeletedTicketScenariosIds = (ticketSummaries: Array<IFormSummaryStory>) =>
		ticketSummaries
			.filter((summary) => !summary.isDeleted)
			.map((summary) => summary?.summaryScenarios || [])
			.flat()
			.filter((scenario) => scenario.isDeleted)
			.map((scenario) => scenario?.id || '');

	const getDeletedTicketCriteriaIds = (ticketSummaries: Array<IFormSummaryStory>) =>
		ticketSummaries
			.filter((summary) => !summary.isDeleted)
			.map((summary) => summary?.summaryAcceptanceCriteria || [])
			.flat()
			.filter((criteria) => criteria.isDeleted)
			.map((criteria) => criteria?.id || '');

	const updateTicketInfo = (data: FormValues) => {
		const { description, ticketSummaries } = data;

		const deletedTicketSummariesIds = getDeletedTicketSummariesIds(ticketSummaries);
		const deletedTicketScenariosIds = getDeletedTicketScenariosIds(ticketSummaries);
		const deletedTicketCriteriaIds = getDeletedTicketCriteriaIds(ticketSummaries);

		const updatedTicketSummaries: Array<ISummaryStoryWithId> = ticketSummaries
			.filter((summary) => !summary.isDeleted)
			.map((summary) => ({
				...summary,
				summaryScenarios: summary.summaryScenarios?.filter((scenario) => !scenario.isDeleted) || [],
				summaryAcceptanceCriteria:
					summary.summaryAcceptanceCriteria?.filter((criteria) => !criteria.isDeleted) || [],
				ticketId: activeTicket?.id || '',
			}));

		editTitleSchema()
			.validate({ title: ticketTitle })
			.then(async (res) => {
				if (res) {
					if (activeTicket) {
						const id = activeTicket.id;

						await dispatch(editUserStories({ userStoriesData: updatedTicketSummaries }));

						if (deletedTicketSummariesIds.length)
							dispatch(
								deleteUserStories({
									storiesIds: deletedTicketSummariesIds,
									ticketId: activeTicket?.id || '',
								})
							);

						if (deletedTicketScenariosIds.length)
							dispatch(
								deleteUserStoriesScenarios({
									scenariosIds: deletedTicketScenariosIds,
									ticketId: activeTicket?.id || '',
								})
							);

						if (deletedTicketCriteriaIds.length)
							dispatch(
								deleteUserStoriesCriteria({
									criteriaIds: deletedTicketCriteriaIds,
									ticketId: activeTicket?.id || '',
								})
							);

						if (currentSelectedSummaryIndex !== -1) {
							dispatch(
								setDetailedViewTicketSummary(updatedTicketSummaries[currentSelectedSummaryIndex])
							);
						}

						dispatch(
							updateLocalTicket({
								id,
								description,
								title: ticketTitle,
								ticketSummaries: updatedTicketSummaries,
							})
						);

						handleTicketChanges({
							oldData: initialData,
							newData: {
								description,
								ticketSummaries,
							},
						});
					}

					saveTicketChanges();
				}
			})
			.catch((error) => {
				setTitleError('title', {
					type: 'manual',
					message: error.message,
				});
			});
	};

	const handleTicketChanges = ({
		oldData,
		newData,
	}: {
		oldData: FormValues;
		newData: FormValues;
	}) => {
		if (activeTicket) {
			dispatch(
				addChangesHistory({
					userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
					ticketId: activeTicket.id,
					ticketStep: TicketStages.general,
					actionType: HistoryActionTypes.userStoriesEdit,
					oldValue: JSON.stringify({
						description: oldData.description,
						ticketSummaries: oldData.ticketSummaries,
					}),
					newValue: JSON.stringify({
						description: newData.description,
						ticketSummaries: newData.ticketSummaries,
					}),
				})
			);
		}
	};

	const handleResetEditForm = useCallback(() => {
		reset();
		dispatch(setEditMode(false));
	}, [reset]);

	const saveTicketChanges = () => {
		dispatch(setIsTicketUpdate(true));
		dispatch(triggerTicketSave());
		dispatch(setEditMode(false));
	};

	return (
		<form
			className="rtl-able"
			style={{
				width: '100%',
				display: 'grid',
				gridTemplateRows: 'auto 0fr',
			}}
			onSubmit={handleSubmit(updateTicketInfo)}
		>
			<Grid
				container
				direction="column"
				sx={{
					flexWrap: 'nowrap',
					overflowY: 'auto',
					height: 'calc(100% - 4px)',
				}}
			>
				<Grid
					item
					sx={{ height: '100%', margin: i18n?.language === 'ar' ? '0 0 0 10px' : '0 10px 0 0' }}
				>
					<TitleText
						text={generalStepTranslate('commonText.description')}
						styles={{ fontWeight: '400' }}
					/>

					<ExpandableTextField
						errors={errors.description}
						registerConfig={register('description')}
						isDisabled={false}
						defaultValue={activeTicket?.description || ''}
					/>

					<TitleText
						text={generalStepTranslate('commonText.summary')}
						styles={{
							marginTop: '10px',
							fontWeight: '400',
						}}
					/>

					<Box sx={{ display: 'flex', justifyContent: 'center', gap: '12px', width: '100%' }}>
						<Box
							sx={{
								...flexMixin({ direction: 'column', gap: 8, justify: 'flex-start' }),
								wordWrap: 'break-word',
								whiteSpace: 'pre-line',
								paddingRight: '10px',
								boxShadow: '0px 4px 16px 0px #2867830F',
								width: currentSelectedSummary ? '50%' : '100%',
							}}
						>
							{summaryStories.map((story, storyIndex) =>
								story?.data?.length ? (
									<Box
										key={story.id}
										sx={{
											...flexMixin({
												justify: 'flex-start',
												align: 'flex-start',
												direction: 'column',
											}),
											width: '100%',
										}}
									>
										<EditUserStoriesDropdown
											header={
												<ExpandableTextField
													errors={errors?.['ticketSummaries']?.[storyIndex]?.['data']}
													registerConfig={register(`ticketSummaries.${storyIndex}.data`)}
													isDisabled={story.isDeleted}
													defaultValue={story?.data}
												/>
											}
											storyIndex={storyIndex}
											isDeleted={story.isDeleted}
											handleDelete={() => handleDeleteSummary(storyIndex)}
											handleRefresh={() => handleRefreshSummary(storyIndex)}
										/>
									</Box>
								) : null
							)}
						</Box>

						<EditDetailedStoryView
							story={currentSelectedSummary}
							storyIndex={currentSelectedSummaryIndex}
							register={register}
							errors={errors}
							handleDeleteScenario={handleDeleteScenario}
							handleRefreshScenario={handleRefreshScenario}
							handleDeleteAcceptanceCriteria={handleDeleteAcceptanceCriteria}
							handleRefreshAcceptanceCriteria={handleRefreshAcceptanceCriteria}
						/>
					</Box>
				</Grid>
			</Grid>

			<Box
				className="not-rtl-able"
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					marginTop: 'auto',
				}}
			>
				<Button
					sx={{
						marginTop: '16px',
						height: '44px',
						width: '133px',
						borderRadius: '8px',
						textTransform: 'none',
						fontSize: '16px',
						fontWeight: '700',
					}}
					variant="outlined"
					color="primary"
					onClick={handleResetEditForm}
				>
					{commonTranslate('buttons.cancel')}
				</Button>

				{updating ? (
					<Button
						sx={{
							marginTop: '16px',
							height: '44px',
							width: '133px',
							borderRadius: '8px',
						}}
						type="submit"
						variant="contained"
						color="primary"
					>
						<Oval
							height={24}
							width={24}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</Button>
				) : (
					<Button
						sx={{
							marginTop: '16px',
							height: '44px',
							width: '133px',
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: '700',
						}}
						type="submit"
						variant="contained"
						color="primary"
						onClick={() => dispatch(setIsTicketUpdate(true))}
					>
						{commonTranslate('buttons.save')}
					</Button>
				)}
			</Box>
		</form>
	);
};
